import { useEffect, useRef, useState } from 'react';
import { useStore, createStore } from '../app/store';
import { ui, type UI } from '../app/ui';
import { contextMenu } from '../app/menu';
import { mapId } from '../firebase';
import type { Model, ZoneShown } from '../data/model';
import { spotOf } from '../data/waiting';
import { middleOf, zonesAt } from '../data/zones';
import { Icon } from '../ui/Icon';
import { loadMaps, onAuthFailure, type MapsState } from './google';
import { escape, icon, ll, makeHtml, mapOptions, styleFor, type HtmlOverlay, type LL } from './overlay';

/**
 * Couriers → Zones: Google's map with each zone the owner drew, in its first courier's colour (the ink with none), the
 * stops waiting for a route as dots in the colour of the zone they're in, and the shop. Drawing a zone: each click adds a
 * corner (no Drawing library: it's deprecated); clicking the first corner, or Done, closes it. Changing a shape uses
 * Google's own editable polygon: drag a corner, or a midpoint to add one; right-click a corner to take it off. The corners
 * are the owner's own clicks: nothing is drawn from Google's positions.
 */

/** The shape being changed, as the owner has dragged it so far (Done reads it). */
export const zoneEdit = createStore<{ path: LL[] | null }>({ path: null });

interface Drawn {
  polys: Map<string, google.maps.Polygon>;
  labels: Map<string, HtmlOverlay>;
  dots: Map<string, HtmlOverlay>;
  shop: HtmlOverlay | null;
  corners: HtmlOverlay[];
  line: google.maps.Polyline | null;
  closing: google.maps.Polyline | null;
  band: google.maps.Polyline | null;
}

const fresh = (): Drawn => ({ polys: new Map(), labels: new Map(), dots: new Map(), shop: null, corners: [], line: null, closing: null, band: null });

const dashes = (color: string, opacity: number): google.maps.IconSequence[] => [
  { icon: { path: 'M 0,-1 0,1', strokeOpacity: opacity, strokeColor: color, scale: 2.5 }, offset: '0', repeat: '10px' },
];

/** Closes the zone being drawn: saved with its name, or "Zone 3" when it has none yet (renamed in its record). */
export function finishDrawing(m: Model, save: (d: { name: string; couriers: string[]; path: LL[] }) => void) {
  const d = ui.get().drawing;
  if (!d || d.path.length < 3) return;
  save({ name: d.name.trim() || `Zone ${m.zones.length + 1}`, couriers: d.couriers, path: d.path });
}

export function ZoneMap({ m, onFinish }: { m: Model; onFinish: () => void }) {
  const s = useStore(ui);
  const box = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const drawn = useRef<Drawn>(fresh());
  const [state, setState] = useState<MapsState>({ state: 'loading' });
  const latest = useRef({ m, s, onFinish });
  latest.current = { m, s, onFinish };

  useEffect(() => {
    let alive = true;
    onAuthFailure((why) => alive && setState({ state: 'failed', why }));
    loadMaps().then(
      () => {
        if (!alive || !box.current) return;
        const map = new google.maps.Map(box.current, mapOptions(ui.get().theme));
        mapRef.current = map;
        // Drawing: each click is a corner. Taken from the pointer itself, not Google's click, which swallows the second
        // of two quick clicks as a double click; a press that moves pans the map instead.
        class Projector extends google.maps.OverlayView {
          onAdd() {}
          draw() {}
          onRemove() {}
        }
        const projector = new Projector();
        projector.setMap(map);
        const el = box.current;
        let down: { x: number; y: number } | null = null;
        el.addEventListener('pointerdown', (e) => (down = e.button === 0 ? { x: e.clientX, y: e.clientY } : null), true);
        el.addEventListener(
          'pointerup',
          (e) => {
            const from = down;
            down = null;
            const d = ui.get().drawing;
            if (!d || !from || e.button !== 0 || Math.hypot(e.clientX - from.x, e.clientY - from.y) > 5) return;
            if ((e.target as HTMLElement | null)?.closest('.corner, .zlabel, .wdot, .shop')) return;
            const r = el.getBoundingClientRect();
            const at = projector.getProjection()?.fromContainerPixelToLatLng(new google.maps.Point(e.clientX - r.left, e.clientY - r.top));
            if (at) ui.set({ drawing: { ...d, path: [...d.path, { lat: at.lat(), lng: at.lng() }] } });
          },
          true,
        );
        // The line from the last corner follows the pointer, so the next edge shows before it's clicked.
        map.addListener('mousemove', (e: google.maps.MapMouseEvent) => {
          const d = ui.get().drawing;
          const band = drawn.current.band;
          if (!d?.path.length || !e.latLng || !band) return;
          band.setOptions({ path: [ll(d.path.at(-1)!), { lat: e.latLng.lat(), lng: e.latLng.lng() }], visible: true });
        });
        box.current.addEventListener('contextmenu', (e) => e.preventDefault());
        setState({ state: 'ready' });
        google.maps.event.addListenerOnce(map, 'idle', () => fit(map, latest.current.m, latest.current.s));
      },
      (e: Error) => alive && setState({ state: 'failed', why: e.message }),
    );
    return () => {
      alive = false;
      const d = drawn.current;
      for (const p of d.polys.values()) p.setMap(null);
      for (const o of [...d.labels.values(), ...d.dots.values(), ...d.corners]) o.overlay.setMap(null);
      d.shop?.overlay.setMap(null);
      for (const l of [d.line, d.closing, d.band]) l?.setMap(null);
      drawn.current = fresh();
      mapRef.current = null;
      zoneEdit.set({ path: null });
    };
  }, []);

  useEffect(() => {
    if (state.state === 'ready' && !mapId) mapRef.current?.setOptions({ styles: styleFor(s.theme) });
  }, [s.theme, state.state]);

  useEffect(() => {
    const map = mapRef.current;
    if (state.state !== 'ready' || !map) return;
    draw(map, drawn.current, m, s, () => latest.current.onFinish());
  }, [m, s, state.state]);

  // A zone picked: framed. Back to none: everything framed.
  useEffect(() => {
    const map = mapRef.current;
    if (state.state === 'ready' && map) fit(map, latest.current.m, latest.current.s);
  }, [s.zone, state.state]);

  return (
    <div className="mapwrap">
      <div className={`gmap${s.drawing ? ' drawing' : ''}`} ref={box} />
      {state.state === 'failed' && (
        <div className="mapnote" role="status">
          <Icon name="mapTrifold" size="lg" />
          <b>Google’s map isn’t showing here</b>
          <span className="cap">{state.why}</span>
          <span className="cap">Zones are drawn on the map, so they need it.</span>
        </div>
      )}
      <div className="mapctl">
        <button data-tip="Show every zone" data-side="left" aria-label="Show every zone" onClick={() => mapRef.current && fit(mapRef.current, m, { ...s, zone: null })}>
          <Icon name="arrowsOut" />
        </button>
        <button data-tip="Zoom in" data-side="left" aria-label="Zoom in" onClick={() => mapRef.current?.setZoom((mapRef.current.getZoom() ?? 13) + 1)}>
          <Icon name="plus" />
        </button>
        <button data-tip="Zoom out" data-side="left" aria-label="Zoom out" onClick={() => mapRef.current?.setZoom((mapRef.current.getZoom() ?? 13) - 1)}>
          <Icon name="minus" />
        </button>
      </div>
    </div>
  );
}

function draw(map: google.maps.Map, d: Drawn, m: Model, s: UI, finish: () => void) {
  const ink = getComputedStyle(document.documentElement).getPropertyValue('--ink').trim() || '#15130F';
  const drawing = !!s.drawing;
  map.setOptions({ draggableCursor: drawing ? 'crosshair' : null, disableDoubleClickZoom: drawing });
  const seen = new Set<string>();
  for (const z of m.zones) {
    seen.add(z.id);
    const picked = s.zone === z.id;
    const editing = picked && s.reshaping;
    let poly = d.polys.get(z.id);
    if (!poly) {
      poly = new google.maps.Polygon({ map, paths: z.path.map(ll) });
      poly.addListener('click', () => {
        if (!ui.get().drawing) ui.set({ zone: z.id, reshaping: false, renaming: false });
      });
      const p = poly;
      poly.addListener('contextmenu', (e: google.maps.PolyMouseEvent) => {
        const ev = e.domEvent as MouseEvent | undefined;
        ev?.preventDefault?.();
        // Changing the shape: right-click a corner takes it off (3 at least).
        if (ui.get().reshaping && ui.get().zone === z.id && e.vertex != null) {
          if (p.getPath().getLength() > 3) p.getPath().removeAt(e.vertex);
          return;
        }
        if (ev) contextMenu(`zone:${z.id}`, ev.clientX, ev.clientY);
      });
      // What the owner drags, kept for Done.
      const keep = () => ui.get().zone === z.id && ui.get().reshaping && zoneEdit.set({ path: p.getPath().getArray().map((x) => ({ lat: x.lat(), lng: x.lng() })) });
      for (const ev of ['set_at', 'insert_at', 'remove_at']) poly.getPath().addListener(ev, keep);
      d.polys.set(z.id, poly);
    }
    // The stored shape, unless it's being changed (the owner's drag stays until Done or Cancel).
    if (!editing && (poly.getEditable() || !samePath(poly, z))) {
      poly.setPaths(z.path.map(ll));
      for (const ev of ['set_at', 'insert_at', 'remove_at']) google.maps.event.clearListeners(poly.getPath(), ev);
      const p = poly;
      const keep = () => ui.get().zone === z.id && ui.get().reshaping && zoneEdit.set({ path: p.getPath().getArray().map((x) => ({ lat: x.lat(), lng: x.lng() })) });
      for (const ev of ['set_at', 'insert_at', 'remove_at']) poly.getPath().addListener(ev, keep);
    }
    if (editing && !poly.getEditable()) zoneEdit.set({ path: z.path.map(ll) });
    poly.setOptions({
      editable: editing,
      clickable: !drawing,
      strokeColor: z.hex,
      strokeOpacity: s.zone && !picked ? 0.5 : 0.95,
      strokeWeight: picked ? 3 : 2,
      fillColor: z.hex,
      fillOpacity: picked ? 0.22 : s.zone ? 0.06 : 0.12,
      zIndex: picked ? 3 : 1,
    });
    let label = d.labels.get(z.id);
    if (!label) {
      const el = document.createElement('div');
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!ui.get().drawing) ui.set({ zone: z.id, reshaping: false, renaming: false });
      });
      el.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        e.stopPropagation();
        contextMenu(`zone:${z.id}`, e.clientX, e.clientY);
      });
      label = makeHtml(map, ll(middleOf(z.path)), el, 20);
      d.labels.set(z.id, label);
    }
    label.el.className = `zlabel${picked ? ' picked' : ''}${s.zone && !picked ? ' dim' : ''}`;
    label.el.style.setProperty('--c', z.hex);
    label.el.style.setProperty('--on', z.on);
    label.el.dataset.ctx = `zone:${z.id}`;
    label.el.innerHTML = `${icon('polygon')}${escape(z.name)}`;
    label.move(ll(middleOf(editing ? (zoneEdit.get().path ?? z.path) : z.path)));
  }
  for (const [id, p] of d.polys) if (!seen.has(id)) (p.setMap(null), d.polys.delete(id));
  for (const [id, o] of d.labels) if (!seen.has(id)) (o.overlay.setMap(null), d.labels.delete(id));

  // The stops waiting for a route, each in the colour of the zone it's in: what each zone would rule.
  const seenDots = new Set<string>();
  for (const w of m.waiting) {
    const at = spotOf(w);
    if (!at) continue;
    seenDots.add(w.id);
    let dot = d.dots.get(w.id);
    if (!dot) {
      const el = document.createElement('div');
      dot = makeHtml(map, ll(at), el, 10);
      d.dots.set(w.id, dot);
    }
    const z: ZoneShown | undefined = zonesAt(at, m.zones)[0] as ZoneShown | undefined;
    dot.el.className = `wdot${z ? '' : ' out'}`;
    dot.el.style.setProperty('--c', z?.hex ?? ink);
    dot.el.dataset.tip = `${w.street}${z ? ` · in ${z.name}` : ' · in no zone'}`;
    dot.move(ll(at));
  }
  for (const [id, o] of d.dots) if (!seenDots.has(id)) (o.overlay.setMap(null), d.dots.delete(id));

  if (m.shop) {
    if (!d.shop) {
      const el = document.createElement('div');
      el.className = 'shop';
      d.shop = makeHtml(map, ll(m.shop), el, 30);
    }
    d.shop.el.innerHTML = `${icon('storefront')}${escape(m.place?.label ?? m.business?.name ?? m.shop.label)}`;
    d.shop.move(ll(m.shop));
  } else if (d.shop) {
    d.shop.overlay.setMap(null);
    d.shop = null;
  }

  // A zone being drawn: its corners, the edges so far, the closing edge dashed, the next edge following the pointer.
  const path = s.drawing?.path ?? [];
  while (d.corners.length > path.length) d.corners.pop()!.overlay.setMap(null);
  path.forEach((p, i) => {
    let o = d.corners[i];
    if (!o) {
      const el = document.createElement('div');
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        // The first corner closes the shape.
        if (i === 0 && (ui.get().drawing?.path.length ?? 0) >= 3) finish();
      });
      o = makeHtml(map, ll(p), el, 35);
      d.corners[i] = o;
    }
    o.el.className = `corner${i === 0 && path.length >= 3 ? ' first' : ''}`;
    o.el.dataset.tip = i === 0 && path.length >= 3 ? 'Close the zone here' : `Corner ${i + 1}`;
    o.move(ll(p));
  });
  if (!d.line) d.line = new google.maps.Polyline({ map, clickable: false, zIndex: 5 });
  if (!d.closing) d.closing = new google.maps.Polyline({ map, clickable: false, zIndex: 5, strokeOpacity: 0 });
  if (!d.band) d.band = new google.maps.Polyline({ map, clickable: false, zIndex: 5, strokeOpacity: 0 });
  d.line.setOptions({ path: path.map(ll), visible: path.length > 1, strokeColor: ink, strokeOpacity: 0.9, strokeWeight: 2 });
  d.closing.setOptions({ path: path.length >= 3 ? [ll(path.at(-1)!), ll(path[0])] : [], visible: path.length >= 3, icons: dashes(ink, 0.6) });
  if (!drawing || !path.length) d.band.setOptions({ visible: false });
  else d.band.setOptions({ icons: dashes(ink, 0.5) });
}

function samePath(poly: google.maps.Polygon, z: ZoneShown): boolean {
  const a = poly.getPath().getArray();
  return a.length === z.path.length && a.every((p, i) => Math.abs(p.lat() - z.path[i].lat) < 1e-9 && Math.abs(p.lng() - z.path[i].lng) < 1e-9);
}

/** Frames the zone picked, else every zone, the waiting stops and the shop. */
function fit(map: google.maps.Map, m: Model, s: Pick<UI, 'zone'>) {
  const pts: LL[] = [];
  const z = s.zone ? m.zones.find((x) => x.id === s.zone) : null;
  if (z) pts.push(...z.path.map(ll));
  else {
    for (const x of m.zones) pts.push(...x.path.map(ll));
    for (const w of m.waiting) {
      const at = spotOf(w);
      if (at) pts.push(ll(at));
    }
    if (m.shop) pts.push(ll(m.shop));
  }
  if (!pts.length) return;
  if (pts.length < 2) {
    map.panTo(pts[0]);
    map.setZoom(14);
    return;
  }
  const b = new google.maps.LatLngBounds();
  pts.forEach((p) => b.extend(p));
  map.fitBounds(b, { top: 60, left: 50, right: 70, bottom: 50 });
}
